import * as uuid from 'uuid';
import type { APIMetainfo } from '../api/ReplicantAPI';
import {
    OTP_REQUEST_FAILURE,
    OTP_REQUEST_SUCCESS,
    OTP_VERIFICATION_FAILURE,
    OTP_VERIFICATION_SUCCESS,
    PostAnalyticsFN,
} from '../common/Analytics';
import type { ErrorReporter } from '../common/ErrorReporter';
import type { OTPRequestOpts, OTPRequestResponse, OTPVerificationOpts } from '../common/OTP';
import { default as DB, OTPVerificationItem } from '../db/DB';
import type { ReplicantConfig } from '../ReplicantConfig';
import type { TwilioClient } from '../utils/Twilio';
import { generateNumericString, unreachable } from '../utils/Utils';
import { APIError } from './APIError';
import type { CryptoUtils } from './utils/CryptoUtils';
import { hashOtpCode, verifyOtpCode } from './utils/OTPUtils';

const OTP_CODE_LENGTH = 6;
const OTP_DEFAULT_TEMPLATE = (otpCode: string, _templateData?: Record<string, string>) =>
    'Your one-time password is ' + otpCode;

export type InitiateOtpOpts = OTPRequestOpts &
    ({ intent: 'addReceiver'; consentText: string; userId: string } | { intent: 'login'; clientAppName?: string });

export class ServerReplicantOTP {
    constructor(
        private opts: {
            config: ReplicantConfig<any>;
            crypto: CryptoUtils;
            dateNow: () => number;
            db: DB<any>;
            errorReporter?: ErrorReporter;
            sendAnalyticsEvents: PostAnalyticsFN;
            twilio: TwilioClient;
        },
    ) {}

    async initiateOtp(opts: InitiateOtpOpts): Promise<OTPRequestResponse> {
        const verificationId = uuid.v4();
        let analyticsUserOrDeviceId = 'userId' in opts ? { user_id: opts.userId } : { device_id: uuid.v4() };
        let phoneNumber: { localePhoneNumber: string; sanitizedPhoneNumber: string } | null = null;

        try {
            if (opts.type !== 'sms') {
                throw Error(`OTP type ${opts.type as any} is not implemented`);
            }
            if (!process.env.APP_SECRET) {
                throw Error('APP_SECRET env var must be configured to use OTP');
            }
            if (process.env.WEB_PLAYABLE !== 'true') {
                throw Error('Web playable mode must be enabled to use OTP');
            }
            if (opts.templateId && !this.opts.config.otp?.smsTemplates?.[opts.templateId]) {
                throw Error('Invalid OTP SMS template ID: ' + opts.templateId);
            }

            // Sanitize phone number:
            phoneNumber = await this.opts.twilio.sanitizePhoneNumber(opts.receiver);
            if (phoneNumber === null) {
                throw APIError.withCode('Invalid phone number', 'server_error', 'invalid_phone_number');
            }
            let verificationItemReceiver = phoneNumber.sanitizedPhoneNumber;

            // Throw error if initiating OTP login with phone number not linked to any user:
            if (opts.intent === 'login') {
                let linkedUserId = await this.opts.db.accountLink.getUserId({
                    clientAppName: opts.clientAppName,
                    phoneNumber: phoneNumber.sanitizedPhoneNumber,
                });

                if (!linkedUserId) {
                    // Fall back to a user linked to the non-sanitized phone number, in order to handle account links created before we added phone number sanitization:
                    linkedUserId = await this.opts.db.accountLink.getUserId({
                        clientAppName: opts.clientAppName,
                        phoneNumber: opts.receiver,
                    });
                    verificationItemReceiver = opts.receiver;
                }

                if (!linkedUserId) {
                    throw APIError.withCode('OTP receiver not found', 'authorization_error', 'invalid_otp_receiver');
                }
                analyticsUserOrDeviceId = { user_id: linkedUserId }; // Attribute OTPRequestSuccess/Failure events correctly to the requesting user in Amplitude
            }

            // Generate OTP code:
            const code = generateNumericString(OTP_CODE_LENGTH);
            const codeHash = hashOtpCode(this.opts.crypto, verificationId, code, process.env.APP_SECRET);

            // Write verification item to DB:
            await this.opts.db.otp.createVerification(
                {
                    clientAppName: opts.intent === 'login' ? opts.clientAppName : undefined,
                    codeHash,
                    consentText: opts.intent === 'addReceiver' ? opts.consentText : '', // Don't store consent text for short-lived verification items
                    id: verificationId,
                    intent: opts.intent,
                    receiver: verificationItemReceiver,
                    type: opts.type,
                },
                this.opts.dateNow(),
            );

            // Send OTP code via SMS:
            const otpTemplate = opts.templateId
                ? this.opts.config.otp!.smsTemplates![opts.templateId]!
                : OTP_DEFAULT_TEMPLATE;
            const smsBody = otpTemplate(code, opts.additionalTemplateData);
            await this.opts.twilio.sendSms({ body: smsBody, receiver: phoneNumber.sanitizedPhoneNumber });
        } catch (error: any) {
            await this.opts.sendAnalyticsEvents([
                {
                    ...analyticsUserOrDeviceId,
                    event_type: OTP_REQUEST_FAILURE,
                    event_properties: { errorMessage: error.message, intent: opts.intent, type: opts.type },
                },
            ]);

            throw error;
        }

        await this.opts.sendAnalyticsEvents([
            {
                ...analyticsUserOrDeviceId,
                event_type: OTP_REQUEST_SUCCESS,
                event_properties: { intent: opts.intent, type: opts.type },
            },
        ]);

        return {
            localePhoneNumber: phoneNumber.localePhoneNumber,
            phoneNumber: phoneNumber.sanitizedPhoneNumber,
            verificationId,
        };
    }

    /** @returns `true` if verification succeeded. */
    async verifyAndLinkUserId(
        opts: OTPVerificationOpts & { entry: { metainfo: APIMetainfo }; userId: string },
    ): Promise<boolean> {
        const intent = 'addReceiver';

        try {
            const verificationItem = await this.verifyOtp({
                code: opts.code,
                verificationId: opts.verificationId,
                intent,
            });

            // If OTP is invalid, track failure event and return `false`:
            if (!verificationItem) {
                await this.opts.sendAnalyticsEvents([
                    {
                        user_id: opts.userId,
                        event_type: OTP_VERIFICATION_FAILURE,
                        event_properties: { intent, errorMessage: 'invalid_otp_code' },
                    },
                ]);

                return false;
            }

            // If OTP code is valid, link user to phone number, track success event and return `true`:
            if (verificationItem.type === 'sms') {
                await this.opts.db.accountLink.linkUserId({
                    clientAppName: opts.entry.metainfo.clientAppName,
                    consentText: verificationItem.consentText,
                    now: this.opts.dateNow(),
                    phoneNumber: verificationItem.receiver,
                    userId: opts.userId,
                });

                opts.entry.metainfo.hasPhoneNumber = true;
            } else {
                throw unreachable(verificationItem.type);
            }

            await this.opts.sendAnalyticsEvents([
                {
                    user_id: opts.userId,
                    event_type: OTP_VERIFICATION_SUCCESS,
                    event_properties: { intent, type: verificationItem.type },
                },
            ]);

            return true;
        } catch (error: any) {
            await this.opts.sendAnalyticsEvents([
                {
                    user_id: opts.userId,
                    event_type: OTP_VERIFICATION_FAILURE,
                    event_properties: { intent, errorMessage: error.message },
                },
            ]);

            throw error;
        }
    }

    /** @throws If verification fails or no linked user is found. */
    async verifyOtpAndGetLinkedUserId(opts: OTPVerificationOpts): Promise<string> {
        try {
            const verificationItem = await this.verifyOtp({
                code: opts.code,
                verificationId: opts.verificationId,
                intent: 'login',
            });

            if (!verificationItem) {
                throw APIError.withCode('OTP verification failed', 'authorization_error', 'invalid_otp_code');
            }

            let linkedUserId: string | null = null;
            if (verificationItem.type === 'sms') {
                linkedUserId = await this.opts.db.accountLink.getUserId({
                    clientAppName: verificationItem.clientAppName,
                    phoneNumber: verificationItem.receiver,
                });
            } else {
                throw unreachable(verificationItem.type);
            }

            if (!linkedUserId) {
                throw Error('No linked user found for verified OTP receiver'); // This should be impossible as you can't request login OTP without a linked number
            }

            await this.opts.sendAnalyticsEvents([
                {
                    user_id: linkedUserId,
                    event_type: OTP_VERIFICATION_SUCCESS,
                    event_properties: { intent: 'login', type: verificationItem.type },
                },
            ]);

            return linkedUserId;
        } catch (error: any) {
            await this.opts.sendAnalyticsEvents([
                {
                    device_id: uuid.v4(),
                    event_type: OTP_VERIFICATION_FAILURE,
                    event_properties: { errorMessage: error.message, intent: 'login' },
                },
            ]);

            throw error;
        }
    }

    /** @returns Verification item if verification succeeds, otherwise `null`. */
    private async verifyOtp(
        opts: OTPVerificationOpts & { intent: 'addReceiver' | 'login' },
    ): Promise<OTPVerificationItem | null> {
        const verificationItem = await this.opts.db.otp.getVerification(
            { id: opts.verificationId, intent: opts.intent },
            this.opts.dateNow(),
        );

        if (
            !verificationItem ||
            !verifyOtpCode(
                this.opts.crypto,
                opts.verificationId,
                opts.code,
                process.env.APP_SECRET!,
                verificationItem.codeHash,
            )
        ) {
            return null;
        }

        return verificationItem;
    }
}
